import { useState, useEffect } from "react";
import { LayoutGrid, List, Shield } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";

/** localStorage-backed view mode (cards | list | group). */
export function useViewMode(storageKey, defaultMode = "cards") {
  const [mode, setMode] = useState(() => {
    try {
      return localStorage.getItem(storageKey) || defaultMode;
    } catch {
      return defaultMode;
    }
  });
  const change = (v) => {
    setMode(v);
    try {
      localStorage.setItem(storageKey, v);
    } catch {
      /* ignore */
    }
  };
  return [mode, change];
}

/** localStorage-backed sort key. */
export function useSortKey(storageKey, defaultKey = "name") {
  const [key, setKey] = useState(() => {
    try {
      return localStorage.getItem(storageKey) || defaultKey;
    } catch {
      return defaultKey;
    }
  });
  const change = (v) => {
    setKey(v);
    try {
      localStorage.setItem(storageKey, v);
    } catch {
      /* ignore */
    }
  };
  return [key, change];
}

export function ViewModeToggle({
  value,
  onChange,
  modes = ["cards", "list", "group"],
  groupLabel = "Per ruolo",
  testid = "view-toggle",
}) {
  const labels = {
    cards: { label: "Card", Icon: LayoutGrid, dt: "view-cards" },
    list: { label: "Elenco", Icon: List, dt: "view-list" },
    group: { label: groupLabel, Icon: Shield, dt: "view-role" },
  };
  return (
    <div
      className="inline-flex bg-slate-100 rounded-full p-1"
      data-testid={testid}
    >
      {modes.map((m) => {
        const meta = labels[m];
        if (!meta) return null;
        const Icon = meta.Icon;
        return (
          <button
            key={m}
            onClick={() => onChange(m)}
            data-testid={meta.dt}
            className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold transition ${
              value === m
                ? "bg-white shadow text-slate-800"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <Icon size={13} /> {meta.label}
          </button>
        );
      })}
    </div>
  );
}

export function SortSelect({ value, onChange, options, testid = "sort-select" }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-44 h-9 text-sm" data-testid={testid}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Stable Italian-aware sort. */
export function sortItems(items, key, accessors = {}) {
  const collator = new Intl.Collator("it", { sensitivity: "base" });
  const arr = [...items];
  const get = (it, k) => (accessors[k] ? accessors[k](it) : it[k]);
  arr.sort((a, b) => {
    if (key === "name" || key === "alpha") {
      const an = `${get(a, "last_name") || ""} ${get(a, "first_name") || ""}`.trim();
      const bn = `${get(b, "last_name") || ""} ${get(b, "first_name") || ""}`.trim();
      return collator.compare(an, bn);
    }
    if (key === "birth_date") {
      const ad = get(a, "birth_date") || "";
      const bd = get(b, "birth_date") || "";
      // empty dates last
      if (!ad && !bd) return 0;
      if (!ad) return 1;
      if (!bd) return -1;
      return ad.localeCompare(bd);
    }
    if (key === "role") {
      const av = get(a, "role") || "";
      const bv = get(b, "role") || "";
      if (av !== bv) return collator.compare(av, bv);
      const an = `${get(a, "last_name") || ""} ${get(a, "first_name") || ""}`.trim();
      const bn = `${get(b, "last_name") || ""} ${get(b, "first_name") || ""}`.trim();
      return collator.compare(an, bn);
    }
    if (key === "jersey") {
      const an = Number(get(a, "jersey_number")) || 9999;
      const bn = Number(get(b, "jersey_number")) || 9999;
      if (an !== bn) return an - bn;
      const ln = `${get(a, "last_name") || ""}`;
      const lb = `${get(b, "last_name") || ""}`;
      return collator.compare(ln, lb);
    }
    return 0;
  });
  return arr;
}
